import { addDaysToDate } from "@utils/general";
import { state, ui } from "./../../../interfaces/interfaces";
import { apiV2 } from "@services/CCMAccess/apiAccess";
import { serviceAgreement } from "@src/utilities/objects";
import { RightioWBSAPIModelsServiceAgreementBillingFrequency, RightioWBSAPIModelsServiceAgreementDuration, RightioWBSAPIModelsServiceAgreementExcess, RightioWBSAPIModelsSPSpServiceAgreementsGetTypeResult, RightioWBSAPIModelsUdtUdtOfferParamsV3 } from "@src/utilities/apis/wbs/model";
import { CareClubCCMAPIControllersApiV2GeneratePreviewInvoicesDto, CareClubCCMAPIModelsViewModelsPlanCostAfterOffersView } from "@src/utilities/apis/ccm/model";

type ApiRequest = CareClubCCMAPIControllersApiV2GeneratePreviewInvoicesDto;
type ApiResponse = CareClubCCMAPIModelsViewModelsPlanCostAfterOffersView;

const getFirstPaymentDay = () => {
  let days = 14;
  let firstPaymentDate = addDaysToDate(days);
  // If the payment date lands on the 29th, 30th or 31st, then force it to the 1st of the next month
  // ----------
  while (firstPaymentDate.getDate() > 28) {
    days++;
    firstPaymentDate = addDaysToDate(days);
  }
  return firstPaymentDate;
};

const calculateDiscount = (apiInput: ApiRequest, apiResponse: ApiResponse): string => {
  const { serviceAgreementDurationId, serviceAgreementBillingFrequencyId } = apiInput;
  const { initialSubscriptionAmount, paymentReocurrenceDescription, adjustmentAmountForFullDuration } = apiResponse;

  let discount = 0;
  let discountDescription = "";

  if (serviceAgreementDurationId === 18 && serviceAgreementBillingFrequencyId) {
    if (serviceAgreementBillingFrequencyId > 3) {
      discount = 12.5;
      discountDescription = `Discount for paying in full: ${discount}%`;
    } else if (serviceAgreementBillingFrequencyId <= 3) {
      discount = 10;
      discountDescription = `Discount for contract length of 18 months: ${discount}%`;
    }
  } else if (serviceAgreementDurationId === 12 && serviceAgreementBillingFrequencyId && serviceAgreementBillingFrequencyId > 3) {
    discount = 7.5;
    discountDescription = `Discount for paying in full: ${discount}%`;
  }

  const firstPaymentDay = getFirstPaymentDay();
  const paymentSummary =
    `First payment of £${initialSubscriptionAmount!.toFixed(2)} is due on ${firstPaymentDay.toLocaleDateString(
      "en-GB"
    )}, then £${initialSubscriptionAmount!.toFixed(2)} per ${paymentReocurrenceDescription}` +
    (discountDescription
      ? ` (this includes ${discountDescription}) [Total discount: £${adjustmentAmountForFullDuration}]`
      : "");

  return paymentSummary;
};

export const calculatePayments = async (
  planType: RightioWBSAPIModelsSPSpServiceAgreementsGetTypeResult,
  billingFrequency: RightioWBSAPIModelsServiceAgreementBillingFrequency,
  duration: RightioWBSAPIModelsServiceAgreementDuration,
  excess: RightioWBSAPIModelsServiceAgreementExcess,
  offers?: RightioWBSAPIModelsUdtUdtOfferParamsV3
): Promise<string> => {

  if (areRequirementsMet(planType, billingFrequency, duration, excess)) {
    const { serviceAgreementTypeId, apiRequest } = prepareApiRequest(
      planType,
      billingFrequency,
      duration,
      excess,
      offers ? [offers] : undefined
    );
    const apiResponse = await fetchPlanCost(apiRequest);

    if (apiResponse) {
      return processResponse(apiResponse, serviceAgreementTypeId, duration, apiRequest);
    }
  }

  return "";
};

const areRequirementsMet = (
  planType: RightioWBSAPIModelsSPSpServiceAgreementsGetTypeResult,
  billingFrequency: RightioWBSAPIModelsServiceAgreementBillingFrequency,
  duration: RightioWBSAPIModelsServiceAgreementDuration,
  excess: RightioWBSAPIModelsServiceAgreementExcess
): boolean => planType !== undefined && billingFrequency !== null && duration !== undefined && excess !== undefined;

const prepareApiRequest = (
  planType: RightioWBSAPIModelsSPSpServiceAgreementsGetTypeResult,
  billingFrequency: RightioWBSAPIModelsServiceAgreementBillingFrequency,
  duration: RightioWBSAPIModelsServiceAgreementDuration,
  excess: RightioWBSAPIModelsServiceAgreementExcess,
  offers: RightioWBSAPIModelsUdtUdtOfferParamsV3[] = []
) => {
  const serviceAgreementTypeId = planType?.serviceAgreementTypeId || 0;
  const apiRequest: ApiRequest = {
    serviceAgreementTypeId,
    contractPeriodFilter: 1,
    serviceAgreementDurationId: duration.serviceAgreementDurationId!,
    serviceAgreementBillingFrequencyId: billingFrequency.serviceAgreementBillingFrequencyId! > 3 ? duration.serviceAgreementDurationId! : billingFrequency.serviceAgreementBillingFrequencyId!,
    serviceAgreementExcessId: excess.serviceAgreementExcessId!,
    offerParams: offers,
  };

  return { serviceAgreementTypeId, apiRequest };
};

const fetchPlanCost = async (apiRequest: ApiRequest): Promise<ApiResponse> => {
  return await apiV2.postApiAgreementsGetPlanCostAfterOffers(apiRequest);
};

const updateServiceAgreement = (apiResponse: ApiResponse, serviceAgreementTypeId: number, durationId: number) => {
  const firstPaymentDay = getFirstPaymentDay();
  const { initialSubscriptionAmount, adjustedFullDurationFee } = apiResponse;

  Object.assign(serviceAgreement.serviceAgreement, {
    feeReoccurring: Number(initialSubscriptionAmount),
    dateFirstPayment: firstPaymentDay,
    dateStart: new Date(),
    feeAnnual: Number(adjustedFullDurationFee),
    serviceAgreementTypeId,
    jobId: state.JobId,
  });

  Object.assign(serviceAgreement.serviceAgreementOffer, {
    allocationContractPeriod: 1,
    serviceAgreementDurationId: durationId,
    serviceAgreementOfferId: ui.CarePlanPlanDetailsOfferOfferType?.serviceAgreementOfferId || 0,
    allocationOrder: 1,
    serviceAgreementOfferAllocationReasonId: ui.CarePlanPlanDetailsOfferOfferReason?.serviceAgreementOfferAllocationReasonId || 0,
  });
};
export const processResponse = (apiResponse: CareClubCCMAPIModelsViewModelsPlanCostAfterOffersView, serviceAgreementTypeId: number, duration: RightioWBSAPIModelsServiceAgreementDuration, apiRequest: CareClubCCMAPIControllersApiV2GeneratePreviewInvoicesDto) => {
  updateServiceAgreement(apiResponse, serviceAgreementTypeId, duration.serviceAgreementDurationId!);
  return calculateDiscount(apiRequest, apiResponse);
}

